/* eslint-disable react-refresh/only-export-components */
import LibZL, { PartialFrameConfig, ZLBoltOptions } from "@zerolight-core/libzl";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useAsync } from "react-use";
import { CloudStreamImageConfigType } from "@zerolight-core/zlthunder-2dod";
import { getDirect2DOptions } from "../utils/getDirect2DOptions";
import { useAppSelector } from "../store/hooks";
import { getInitialUrlArgs } from "../UrlArgsProvider";
import { clone } from "../utils/clone";
import { requireToken, useToken } from "../utils/auth";

export const Direct2DContext = createContext<ReturnType<typeof initDirect2D> | Promise<undefined>>((async () => {return undefined})())

async function initDirect2D(options: ZLBoltOptions)
{
    return await new LibZL().direct2D("zlthunder.net").then(r => r.connectWithMetadata(options)).catch(e =>
    {
        console.log("Failed to get Direct2D instance");
        throw e;
    });
}

export function useProxy(config: Partial<PartialFrameConfig> | Partial<CloudStreamImageConfigType> | undefined)
{
    const direct2d = useContext(Direct2DContext);
    const instance = useAsync(() => direct2d);
    const d2dInstance = instance.value?.interface2D;

    const frameConfig = useMemo(() =>
    {
        if (!config)
            return undefined;

        const frameConfig = {
            ...config,
        }

        try
        {
            return [
                d2dInstance?.makeCarProxyConfig(clone(frameConfig)),
                d2dInstance?.makeCarProxyConfig({ ...clone(frameConfig), export_format: "usdz" })
            ]
        } catch (e)
        {
            console.warn(e);
            return undefined
        }
    }, [d2dInstance, config]);
    const request = useMemo(() => d2dInstance?.fetchFrames(frameConfig ? frameConfig : [], { autoStart: true, generateMissing: true, loadImages: false, loadMetadata: false }), [d2dInstance, frameConfig]);
    const firstFrame = Object.values(request?.frames ?? {})?.[0];
    return firstFrame;
}

export function useFrame(config: Partial<PartialFrameConfig> | Partial<CloudStreamImageConfigType> | undefined)
{
    const direct2d = useContext(Direct2DContext);
    const instance = useAsync(() => direct2d);
    const d2dInstance = instance.value?.interface2D;

    const frameConfig = useMemo(() =>
    {
        if (!config)
            return undefined;

        const frameConfig = {
            ...config,
        }
        try
        {
            if ("appSelect" in frameConfig)
                return d2dInstance?.makeCloudStreamImageConfig(clone(frameConfig) as CloudStreamImageConfigType)
            else
                return d2dInstance?.makeFrameConfig(clone(frameConfig) as PartialFrameConfig);
        } catch (e)
        {
            console.warn(e);
            return undefined
        }
    }, [d2dInstance, config]);
    const request = useMemo(() => !frameConfig ? undefined : d2dInstance?.fetchFrames([frameConfig], { autoStart: true, generateMissing: true, loadImages: false, loadMetadata: false }), [d2dInstance, frameConfig]);
    const firstFrame = Object.values(request?.frames ?? {})?.[0];
    return firstFrame;
}

export function Direct2D(props: React.PropsWithChildren)
{
    const visualizerConfig = useAppSelector(s => s.configuration.currentVisualizerConfig);
    const params = getInitialUrlArgs();
    const service = params.assetService; 
    const options = getDirect2DOptions(visualizerConfig, service!);
    const needToken = requireToken(service)
    const token = useToken();
    const [generation, setGeneration] = useState(0);

    const optionsStr = JSON.stringify(options)
    const hasToken = !!token
    const tokenStr = needToken ? JSON.stringify(token) : "{}"

    const ctxData = useMemo(() => (hasToken || !needToken) && initDirect2D({
        ...JSON.parse(optionsStr),
        ...JSON.parse(tokenStr),
    }).then(d =>
    {
        console.warn("Got Direct2D instance", d);

        const interval = setInterval(() => d.interface2D.waitUntilConnected().catch(onError), 5 * 60 * 1000);

        function onError(error: {errorCode: string} | string)
        {
            console.warn("Direct2D error", error);
            setGeneration(s => s + 1);
            clearInterval(interval);

            d.interface2D.off("onError", onError);
            d.interface2D.off("clientDestroy", onError);
        }
        d.interface2D.on("onError", onError);
        d.interface2D.on("clientDestroy", onError);

        return d;
    }), [needToken, hasToken, optionsStr, tokenStr, generation, setGeneration]);

    if (!ctxData)
        return <></>;

    return <Direct2DContext.Provider value={ctxData}>
        {props.children}
    </Direct2DContext.Provider>
}

export function useDirect2D()
{
    const direct2d = useContext(Direct2DContext);
    return useAsync(() => direct2d, [direct2d]).value?.interface2D;
}